import { supabase, allowOfflineFallback } from './mockData';
import { Appointment } from '../types';

export async function getAppointments(): Promise<Appointment[]> {
  try {
    const { data, error } = await supabase
      .from('appointments')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    
    // Pet bilgilerini manuel olarak bağla
    const appointments = data as Appointment[];
    const { data: petsData, error: petsError } = await supabase
      .from('pets')
      .select(`
        *,
        owner:owners(id, name, phone, email, address)
      `);
    if (petsError) throw new Error(petsError.message);

    const pets = (petsData || []).map((item: any) => ({
      id: item.id,
      name: item.name || '',
      species: item.species || 'dog',
      breed: item.breed || '',
      age: item.age || 0,
      weight: item.weight || 0,
      color: item.color || '',
      owner_id: item.owner_id || '',
      owner: item.owner ? {
        id: item.owner.id,
        name: item.owner.name || '',
        phone: item.owner.phone || '',
        email: item.owner.email || '',
        address: item.owner.address || '',
        chipNumber: item.owner.chip_number || '',
        passportNumber: item.owner.passport_number || '',
        createdAt: item.owner.created_at || new Date().toISOString()
      } : undefined,
      createdAt: item.created_at || new Date().toISOString()
    }));
    
    return appointments.map(appointment => ({
      ...appointment,
      pet: pets.find(p => p.id === appointment.pet_id || p.id === appointment.petId)
    })) as Appointment[];
  } catch (error) {
    console.error('getAppointments:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const { mockAppointments, getAppointmentWithPet } = await import('./mockData');
    return mockAppointments.map(appointment => {
      const appointmentWithPet = getAppointmentWithPet(appointment.id);
      return appointmentWithPet || appointment;
    });
  }
}

// Yalnızca gerçek sütunlar gönderilir (pet, createdAt, petId gibi türetilmiş alanlar sızmasın)
const toRow = (a: Partial<Appointment>): Record<string, unknown> => {
  const row: Record<string, unknown> = {};
  const petId = a.pet_id ?? a.petId;
  if (petId !== undefined) row.pet_id = petId;
  if (a.date !== undefined) row.date = a.date.split('T')[0];
  if (a.time !== undefined) row.time = a.time;
  if (a.type !== undefined) row.type = a.type;
  if (a.status !== undefined) row.status = a.status;
  if (a.notes !== undefined) row.notes = a.notes;
  return row;
};

export async function addAppointment(appointment: Omit<Appointment, 'id' | 'createdAt'>): Promise<Appointment> {
  try {
    const payload = toRow(appointment);
    
    const { data, error } = await supabase.from('appointments').insert([payload]).select().single();
    if (error) throw new Error(error.message);
    return data as Appointment;
  } catch (error) {
    console.error('appointments insert:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const newAppointment: Appointment = {
      ...appointment,
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    };
    
    const existingAppointments = JSON.parse(localStorage.getItem('appointments') || '[]');
    existingAppointments.push(newAppointment);
    localStorage.setItem('appointments', JSON.stringify(existingAppointments));
    
    return newAppointment;
  }
}

export async function updateAppointment(id: string, appointment: Partial<Appointment>): Promise<Appointment> {
  try {
    const { data, error } = await supabase.from('appointments').update(toRow(appointment)).eq('id', id).select().single();
    if (error) throw new Error(error.message);
    return data as Appointment;
  } catch (error) {
    console.error('appointments update:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const existingAppointments = JSON.parse(localStorage.getItem('appointments') || '[]');
    const appointmentIndex = existingAppointments.findIndex((a: Appointment) => a.id === id);
    
    if (appointmentIndex !== -1) {
      existingAppointments[appointmentIndex] = { ...existingAppointments[appointmentIndex], ...appointment };
      localStorage.setItem('appointments', JSON.stringify(existingAppointments));
      return existingAppointments[appointmentIndex];
    }
    
    throw new Error('Appointment bulunamadı');
  }
}

export async function deleteAppointment(id: string): Promise<void> {
  try {
    const { error } = await supabase.from('appointments').delete().eq('id', id);
    if (error) throw new Error(error.message);
  } catch (error) {
    console.error('appointments delete:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const existingAppointments = JSON.parse(localStorage.getItem('appointments') || '[]');
    const filteredAppointments = existingAppointments.filter((a: Appointment) => a.id !== id);
    localStorage.setItem('appointments', JSON.stringify(filteredAppointments));
  }
}